/**
 * Accordion block behaviour (theme-native block `acf/accordion`).
 *
 * IMPORTANT: this intentionally uses its own class namespace
 * (`js-accordion-block` / `js-accordion-trigger` / `js-accordion-panel`) so it
 * never collides with the third-party "Accordion Blocks" plugin, which binds to
 * `.js-accordion-item` / `.js-accordion-controller`. Keeping the namespaces
 * separate means both can coexist on the same page without double-binding.
 *
 * The open/close animation is handled in CSS (the `.is-open` class on the item);
 * this module only keeps the class and the ARIA state in sync, so the logic is
 * fully unit-testable without a real DOM.
 */

const ITEM_SELECTOR = '.js-accordion-block';
const TRIGGER_SELECTOR = '.js-accordion-trigger';
const PANEL_SELECTOR = '.js-accordion-panel';
const OPEN_CLASS = 'is-open';

/**
 * Whether an accordion item is currently open (null-safe).
 */
function isAccordionOpen(item) {
  return Boolean(item) && item.classList.contains(OPEN_CLASS);
}

/**
 * Apply an open/closed state to an item, keeping class + ARIA coherent.
 *
 * @param {{item, trigger, panel}} elements
 * @param {boolean} isOpen
 * @returns {boolean} the applied state
 */
function setAccordionState(elements, isOpen) {
  const { item, trigger, panel } = elements;

  if (isOpen) {
    item.classList.add(OPEN_CLASS);
  } else {
    item.classList.remove(OPEN_CLASS);
  }

  trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

  return isOpen;
}

/**
 * Resolve the trigger/panel that belong to a single accordion item.
 */
function getAccordionElements(item) {
  return {
    item,
    trigger: item.querySelector(TRIGGER_SELECTOR),
    panel: item.querySelector(PANEL_SELECTOR),
  };
}

/**
 * Wire a single accordion item: sync ARIA with the server-rendered state, then
 * toggle on trigger click. Returns the resolved elements, or `null` when the
 * markup is incomplete (defensive — never throws).
 */
function initAccordionItem(item) {
  const elements = getAccordionElements(item);

  if (!elements.trigger || !elements.panel) {
    return null;
  }

  // Mirror the server-rendered open state (ACF `initially_open`) into ARIA.
  setAccordionState(elements, isAccordionOpen(item));

  elements.trigger.addEventListener('click', () => {
    setAccordionState(elements, !isAccordionOpen(item));
  });

  return elements;
}

/**
 * Initialise every accordion item within the given scope (defaults to the
 * whole document). Each item toggles independently.
 *
 * @param {ParentNode} [root]
 * @returns {Array} the elements wired up
 */
function initAccordion(root) {
  const scope = root || (typeof document !== 'undefined' ? document : null);

  if (!scope) {
    return [];
  }

  return Array.from(scope.querySelectorAll(ITEM_SELECTOR))
    .map(initAccordionItem)
    .filter(Boolean);
}

module.exports = {
  isAccordionOpen,
  setAccordionState,
  getAccordionElements,
  initAccordionItem,
  initAccordion,
};
